/** Project contracts — spec §6.4, §7.3. */
import { z } from 'zod';
import { CUSTOM_FIELD_TYPES, MAX_CUSTOM_FIELDS_PER_PROJECT, PROJECT_ROLES, PROJECT_STATUSES, PROJECT_VISIBILITIES, STATUS_CATEGORIES, TASK_TYPES, } from './enums.js';
import { hexColourSchema, objectIdSchema, richTextSchema } from './common.js';
export const PROJECT_KEY_RE = /^[A-Z][A-Z0-9]{1,4}$/;
export const projectKeySchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(PROJECT_KEY_RE, 'Key must be 2–5 uppercase characters, starting with a letter');
export const projectStatusDefSchema = z.object({
    id: z.string().min(1).max(40),
    name: z.string().min(1).max(40),
    category: z.enum(STATUS_CATEGORIES),
    colour: hexColourSchema,
    order: z.number().int().min(0),
    wipLimit: z.number().int().min(1).max(999).nullish(),
    requiresReason: z.boolean().optional(),
});
export const customFieldDefSchema = z.object({
    id: z.string().min(1).max(40),
    name: z.string().min(1).max(60),
    type: z.enum(CUSTOM_FIELD_TYPES),
    options: z.array(z.string().min(1).max(80)).max(50).optional(),
    required: z.boolean().default(false),
    order: z.number().int().min(0).default(0),
});
export const projectSettingsSchema = z.object({
    qaEnabled: z.boolean().default(false),
    requireReviewBeforeDone: z.boolean().default(false),
    allowGuestComments: z.boolean().default(false),
    defaultAssigneeId: objectIdSchema.nullish(),
    defaultTaskType: z.enum(TASK_TYPES).default('feature'),
    autoArchiveDoneAfterDays: z.number().int().min(1).max(365).nullish(),
    wipEnforced: z.boolean().default(false),
    timeTrackingRequired: z.boolean().default(false),
});
export const projectMemberSchema = z.object({
    userId: objectIdSchema,
    projectRole: z.enum(PROJECT_ROLES),
});
/** V4 F-1 — what a project needs to explain itself to a newcomer. */
export const PROJECT_TYPES = [
    'client',
    'product',
    'internal',
    'research',
    'marketing',
    'other',
];
export const projectLinkSchema = z
    .object({
    label: z.string().min(1).max(60),
    // http(s) only: a project link is followed by a click, and `javascript:`
    // in an href is a stored XSS with extra steps.
    url: z.string().url().max(500).refine((u) => /^https?:\/\//i.test(u), {
        message: 'Links must start with http:// or https://',
    }),
})
    .strict();
export const projectContactSchema = z
    .object({
    userId: objectIdSchema,
    /** Free text — "Client contact", "QA owner". Roles here are not permissions. */
    role: z.string().min(1).max(60),
})
    .strict();
export const projectAboutSchema = z
    .object({
    summary: richTextSchema.optional(),
    projectType: z.enum(PROJECT_TYPES).optional(),
    techStack: z.array(z.string().min(1).max(40)).max(40).default([]),
    links: z.array(projectLinkSchema).max(20).default([]),
    contacts: z.array(projectContactSchema).max(20).default([]),
})
    .strict();
export const createProjectSchema = z
    .object({
    name: z.string().min(1).max(120),
    key: projectKeySchema,
    description: z.string().max(2000).optional(),
    colour: hexColourSchema.default('#2F6F4E'),
    icon: z.string().min(1).max(8).default('📁'),
    status: z.enum(PROJECT_STATUSES).default('active'),
    /**
     * Omit to inherit `workspace.settings.defaultProjectVisibility` — V4 D-1.1.
     * No client-side default: a default here would silently win over the
     * workspace's choice, which is the whole point of the setting.
     */
    visibility: z.enum(PROJECT_VISIBILITIES).optional(),
    leadId: objectIdSchema,
    teamIds: z.array(objectIdSchema).default([]),
    members: z.array(projectMemberSchema).default([]),
    startDate: z.coerce.date().optional(),
    targetDate: z.coerce.date().optional(),
    settings: projectSettingsSchema.partial().optional(),
    /** V4 F-1 — optional at creation, editable later by lead or admin. */
    about: projectAboutSchema.partial().optional(),
    /**
     * V3 R4 — seeds the Purpose section of the pinned "📌 Project Guide" that
     * every new project gets. Optional: skipping it still creates the guide,
     * with the Purpose section left as a prompt to fill in.
     */
    guidePurpose: z.string().max(4000).optional(),
})
    .strict();
/** `key` is immutable after creation (§4.2) so it is not patchable. */
export const updateProjectSchema = createProjectSchema
    .omit({ key: true, members: true, guidePurpose: true })
    .partial()
    .strict();
export const replaceStatusesSchema = z
    .object({
    statuses: z
        .array(projectStatusDefSchema.partial({ id: true, order: true }).extend({
        name: z.string().min(1).max(40),
        category: z.enum(STATUS_CATEGORIES),
    }))
        .min(1)
        .max(20),
})
    .strict();
export const deleteStatusQuerySchema = z
    .object({ migrateTo: z.string().min(1).optional() })
    .strict();
export const addProjectMemberSchema = projectMemberSchema.strict();
export const updateProjectMemberSchema = z
    .object({ projectRole: z.enum(PROJECT_ROLES) })
    .strict();
export const deleteProjectSchema = z.object({ confirmKey: z.string().min(1) }).strict();
export const listProjectsQuerySchema = z
    .object({
    q: z.string().max(120).optional(),
    status: z.enum(PROJECT_STATUSES).optional(),
    includeArchived: z.coerce.boolean().default(false),
    memberId: objectIdSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
})
    .strict();
export const customFieldCreateSchema = customFieldDefSchema
    .omit({ id: true })
    .strict();
export const customFieldUpdateSchema = customFieldCreateSchema.partial().strict();
export const MAX_CUSTOM_FIELDS = MAX_CUSTOM_FIELDS_PER_PROJECT;
//# sourceMappingURL=project.js.map